/**
 * Private Route Component
 *
 * Protects routes that require:
 * 1. Firebase authentication
 * 2. GDPR consent recorded
 * 3. Health survey completed (for /chat - Dual Memory pattern)
 *
 * Redirects to:
 * - Landing page if not authenticated or consent pending
 * - /mi-salud if survey not completed (except when already on /mi-salud)
 *
 * @see Issue #177 - Frontend Firebase Auth Integration
 * @see PLAN_IMPLEMENTACION.md - Dual Memory Pattern
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export default function PrivateRoute() {
    const { isAuthenticated, isLoading, consentRequired, surveyCompleted } = useAuth();
    const location = useLocation();

    // Show loading spinner while checking auth state
    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    <p className="text-sm text-muted-foreground">Verificando sesión...</p>
                </div>
            </div>
        );
    }

    // Redirect to landing if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    // Redirect to landing if consent still required
    if (consentRequired) {
        return <Navigate to="/" replace />;
    }

    // Dual Memory Pattern: Require survey completion before chat access
    // This ensures critical medical information (allergies, conditions) is available
    // Exception: Allow access to /mi-salud so users can complete the survey
    const requiresSurvey = location.pathname === '/chat';
    const isOnSurveyPage = location.pathname === '/mi-salud';
    
    if (requiresSurvey && !surveyCompleted && !isOnSurveyPage) {
        // Save the intended destination so we can redirect after survey completion
        return <Navigate to="/mi-salud" replace state={{ from: location, reason: 'survey_required' }} />;
    }

    // Render protected content
    return <Outlet />;
}
